<script setup lang="ts">
import { Head, Link } from '@inertiajs/vue3'
import PageHeading from "@/Components/Headings/PageHeading.vue"
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import Button from '@/Components/Utils/Button.vue'
import { useFormatTime } from '@/Composables/useFormatTime'
import Icon from '@/Components/Utils/Icon.vue'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faStickyNote, faUndoAlt, faInfoCircle } from '@fal'
import { library } from '@fortawesome/fontawesome-svg-core'
import Tag from '@/Components/Utils/Tag.vue'
import { useFormatCurrency } from '@/Composables/useFormatCurrency'
import { computed, inject, onMounted, onUnmounted, ref } from 'vue'
import { layoutStructure } from '@/Composables/useLayoutStructure'
import ModalConfirmationDelete from '@/Components/Utils/ModalConfirmationDelete.vue'
import IconManufacture from '@/Components/Utils/IconManufacture.vue'
import { Fieldset } from 'primevue'
import { OrderResource } from '@/types/Resources'
library.add(faStickyNote, faUndoAlt, faInfoCircle)



const props = defineProps<{
    pageHead: PageHeadingTS
    title?: string
    data: OrderResource
}>()

const isVisitEdit = ref(false)
const routeCurrent = route().current()

const layout = inject('layout', layoutStructure)

const generateLinkOrder = (slug?: string) => {
    if (!slug) return null

    switch (routeCurrent) {
        case 'sales.orders.show':
            // return route('sales.manufactures.show', slug)
            return null  // TODO
        case 'master.orders-m.show':
            return route('master.orders-m.show', slug)
        default:
            return null
    }
}

const generateLinkManufacture = (slug?: string) => {
    if (!slug) return null

    switch (routeCurrent) {
        case 'sales.orders.show':
            return route('sales.manufactures.show', slug)
        case 'master.orders-m.show':
            return route('manufacture.show', slug)
        default:
            return null
    }
}

const generateLinkInvoice = (slug?: string) => {
    if (!slug) return null
    const newSlug = slug.toLowerCase()?.endsWith('-c') ? slug.slice(0, -2) : slug
    switch (layout?.auth?.role) {
        case 'super_admin':
            return route('master.invoices.show', newSlug)
        case 'sales':
            return route('sales.invoices.show', newSlug)
        default:
            return null
    }
}

const generateLinkProduct = (slug?: string) => {
    if (!slug) return null
    
    switch (layout?.auth?.role) {
        case 'super_admin':
        case 'admin':
            return route('master.products.show', slug)
        default:
            return null
    }
}

const generateLinkBrand = (slug?: string) => {
    if (!slug) return null

    switch (routeCurrent) {
        case 'sales.orders.show':
            return route('sales.brands.show', slug)
        default:
            return null
    }
}

const linkSales = computed(() => {
    const username = props.data?.sales?.username
    if (!username) return null

    switch (layout?.auth?.role) {
        case 'super_admin':
        case 'admin':
            return route('master.sales-m.index.show', username)
        default:
            return null
    }
})


onMounted(() => {
    if (routeCurrent === 'master.orders-m.show') {
        layout.root_active = 'master.orders-m.' + props.data?.type
    } else {
        layout.root_active = 'sales.orders.' + props.data?.type

    }
})

onUnmounted(() => {
    layout.root_active = ''
})
</script>

<template>
    <Head :title="title" />
    <PageHeading :data="pageHead">
        <template #afterTitle>
            <Tag v-if="data.canceled_at" :label="data.state_icon?.tooltip" :theme="7" noHoverColor />
        </template>

        <template #button-editbutton="{ action }">
            <Link
                :href="action.route?.name ? route(action.route?.name, action.route?.parameters) : action.href?.name ? route(action.href?.name, action.href?.parameters) : '#'"
                class=""
                :method="action.route?.method || 'get'"
                as="a"
                :target="action.target"
                @start="() => isVisitEdit = true"
                @finish="() => isVisitEdit = false"
            >
                <Button
                    :style="action.style"
                    :label="action.label"
                    :size="action.size"
                    :icon="action.icon"
                    :loading="isVisitEdit"
                    :disabled="action.disabled"
                    :iconRight="action.iconRight"
                    :key="`ActionButton${action.label}${action.style}`"
                    :tooltip="data.edited_at ? `Order sudah pernah diedit (${useFormatTime(data.edited_at)})` : undefined"
                >
                </Button>
            </Link>
        </template>
        
        <!-- Button: batal -->
        <template #button-cancel-order="{ action }">
            <div>
                <ModalConfirmationDelete
                    :routeDelete="action.route"
                    isFullLoading
                    title="Batalkan order?"
                    description="Membatalkan order, juga akan membatalkan manufaktur dan order tidak akan ditagihkan di invoice."
                    yesLabel="Ya, batalkan order"
                    yesIcon="fal fa-undo-alt"
                >
                    <template #default="{ isOpenModal, changeModel, isLoadingdelete }">
                        <Button
                            @click="() => changeModel()"
                            :style="action.style"
                            :label="action.label"
                            :loading="isLoadingdelete"
                            :icon="action.icon"
                            :iconRight="action.iconRight"
                            :key="`ActionButton${action.label}${action.style}`"
                            :tooltip="action.tooltip"
                        />
                    </template>
                </ModalConfirmationDelete>
            </div>
        </template>
        
        <!-- Button: batal -->
        <template #button-delete-button="{ action }">
            <div>
                <ModalConfirmationDelete
                    :routeDelete="action.route"
                    isFullLoading
                    title="Hapus order?"
                    description="Menghapus order juga akan menghapus manufaktur yang berkaitan"
                    yesLabel="Ya, hapus order"
                    yesIcon="fal fa-trash-alt"
                >
                    <template #default="{ isOpenModal, changeModel, isLoadingdelete }">
                        <Button
                            @click="() => changeModel()"
                            :style="action.style"
                            :label="action.label"
                            :loading="isLoadingdelete"
                            :icon="action.icon"
                            :iconRight="action.iconRight"
                            :key="`ActionButton${action.label}${action.style}`"
                            :tooltip="action.tooltip"
                        />
                    </template>
                </ModalConfirmationDelete>
            </div>
        </template>
    </PageHeading>

    
    <!-- <pre>{{ data }}</pre> -->
    <div class="mx-8 max-w-5xl mt-6 grid sm:grid-cols-5 gap-x-6 gap-y-8 xl:gap-x-8 ">
        <div class="sm:col-span-3 w-full overflow-hidden rounded-xl border border-gray-300">
            <div class=" flex flex-col justify-center gap-x-4 border-b border-gray-900/5 p-6"
                :class="data.billing_date ? 'bg-green-50' : 'bg-gray-50'"
            >
                <div class="flex justify-between">
                    <div class="font-bold">
                        {{ data.reference }}
                    </div>
                    <div v-if="data.billing_date" v-tooltip="'Tgl pelunasan: ' + useFormatTime(data.billing_date)">
                        <Tag :label="data.billing_status_label" :theme="3" />
                    </div>
                </div>
                <div v-if="data.note" class="text-sm/6 text-gray-500 w-fit">
                    <FontAwesomeIcon icon='fal fa-sticky-note' class='' fixed-width aria-hidden='true' />
                    {{ data.note }}
                    
                </div>
            </div>
            
            <dl class="-my-3 divide-y divide-gray-100 px-6 py-4 text-sm/6">                    
                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Tgl. dibuat</dt>
                    <dd class="flex items-start gap-x-2 font-medium">
                        {{ useFormatTime(data.created_at) }}
                    </dd>
                </div>
                
                <div v-if="layout?.auth?.role != 'sales'" class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Sales</dt>
                    <dd class="flex items-start gap-x-2 font-medium">
                        <Link v-if="linkSales" :href="linkSales" class="secondaryLink">
                            {{ data.sales?.name }}
                        </Link>
                        <span v-else>
                            {{ data.sales?.name ?? '-' }}
                        </span>
                    </dd>
                </div>

                <!-- Desain -->
                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Desain</dt>
                    <dd class="flex items-start gap-x-2 font-medium">
                        {{ data.design_type_label }}
                    </dd>
                </div>

                <!-- Invoice -->
                <div v-if="generateLinkInvoice(data.invoice?.slug)" class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Invoice</dt>
                    <dd class="flex items-start gap-x-2 font-medium">
                        <Link :href="(generateLinkInvoice(data.invoice?.slug) as string)" class="primaryLink">
                            {{ data.invoice?.reference.endsWith('-C') ? data.invoice?.reference.replace('-C', ' (cash)') : data.invoice?.reference  }}
                        </Link>
                    </dd>
                </div>

                <!-- Product -->
                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Nama Produk</dt>
                    <dd class="text-right gap-x-2 font-medium">
                        <div v-if="generateLinkProduct(data.product_slug)">
                            <Link :href="(generateLinkProduct(data.product_slug) as string)" class="primaryLink">
                                <Icon :data="data.product?.type_icon" />
                                {{ data.product?.name || '-' }}
                            </Link>
                            <div class="text-gray-400 font-normal italic">
                                {{ data.product?.code ? `(${data.product?.code})` : '' }}
                            </div>
                        </div>
                        
                        <span v-else-if="data.product_slug" class="">
                            <Icon :data="data.product?.type_icon" />
                            {{ data.product?.name || '-' }}
                            <div class="text-gray-400 font-normal italic">
                                {{ data.product?.code ? `(${data.product?.code})` : '' }}
                            </div>
                        </span>

                        <span v-else class="text-gray-400 italic">
                            <Icon :data="data.product?.type_icon" />
                            Produk tidak ditemukan
                            <FontAwesomeIcon v-tooltip="'Produk mungkin telah dihapus dari sistem'" icon='fal fa-info-circle' class='text-gray-400' fixed-width aria-hidden='true' />
                        </span>
                    </dd>
                </div>

                
                <!-- Field: Keterangan order -->
                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Keterangan order</dt>
                    <dd class="text-right gap-x-2 font-medium">
                        {{ data.keterangan_cetak || '-' }}
                    </dd>
                </div>

                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Harga</dt>
                    <dd class="flex font-semibold">
                        {{ useFormatCurrency(data.product?.price) || '-' }}<span class="inline font-normal text-gray-400">/pcs</span>
                    </dd>
                </div>

                <!-- Jumlah Order -->
                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">
                        Jml. Order
                    </dt>
                    <dd class="flex items-center gap-x-2 font-semibold">
                        {{ data.quantity ?? 0 }} pcs
                        <FontAwesomeIcon v-tooltip="'Jumlah pcs yang dimasukkan ke estimasi biaya'" v-if="!data.manufacture?.finishing_count && !data.manufacture?.validated_count && !data.manufacture?.count_validate_from_customer" icon='fas fa-circle' class='text-cyan-500 animate-pulse baseline text-xxs' fixed-width aria-hidden='true' />
                    </dd>
                </div>

                <!-- Brand -->
                <div v-if="data.product?.type === 'tutup' || data.product?.type === 'custom'" class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500 flex items-center gap-x-1">
                        Brand
                        <FontAwesomeIcon icon='fal fa-copyright' class='text-gray-500' fixed-width aria-hidden='true' />
                    </dt>
                    <dd class="flex items-start gap-x-1">
                        <Link v-if="generateLinkBrand(data.brand?.slug)" :href="(generateLinkBrand(data.brand?.slug) as string)" class="font-medium primaryLink">
                            {{ data.brand?.name }}
                        </Link>
                        <span v-else>
                            {{ data.brand?.name || '-' }}
                        </span>
                    </dd>
                </div>

                <!-- Manufaktur -->
                <div v-if="data.manufacture" class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Manufaktur</dt>
                    <dd class="text-right gap-x-1">
                        <span class="inline-flex">
                            <IconManufacture :data="data.manufacture?.state_icon" />
                        </span>

                        <Link v-if="generateLinkManufacture(data.manufacture?.slug)" :href="(generateLinkManufacture(data.manufacture?.slug) as string)" class="font-medium secondaryLink">
                            {{ data.manufacture?.reference || '-' }}
                        </Link>
                        <span v-else>
                            {{ data.manufacture?.reference || '-' }}
                        </span>
                    </dd>
                </div>
                
                <div>
                    <!-- Jumlah Finishing -->
                    <div v-if="data.product?.type === 'custom'" class="border-l-4 border-gray-400 pl-2.5 flex justify-between gap-x-4 py-3">
                        <dt class="text-gray-500">
                            Jml. finishing
                        </dt>
                        <dd v-if="data.manufacture?.finishing_count !== null" class="flex items-center gap-x-2">
                            {{ data.manufacture?.finishing_count ?? 0 }} pcs
                            <FontAwesomeIcon v-tooltip="'Jumlah pcs yang dimasukkan ke estimasi biaya'" v-if="data.manufacture?.finishing_count && !data.manufacture?.validated_count && !data.manufacture?.count_validate_from_customer" icon='fas fa-circle' class='text-cyan-500 animate-pulse baseline text-xxs' fixed-width aria-hidden='true' />
                        </dd>
                        <dd v-else class="flex items-start gap-x-2">
                            -
                        </dd>
                    </div>
                    
                    <!-- Jumlah Validasi -->
                    <div v-if="data.product?.type === 'custom'" class="border-l-4 border-gray-400 pl-2.5 flex justify-between gap-x-4 py-3">
                        <dt class="text-gray-500">
                            Jml. validasi di Manufacture
                        </dt>
                        <dd v-if="data.manufacture?.validated_count !== null" class="flex items-center gap-x-2">
                            {{ data.manufacture?.validated_count ?? 0 }} pcs
                            <FontAwesomeIcon v-tooltip="'Jumlah pcs yang dimasukkan ke estimasi biaya'" v-if="data.manufacture?.validated_count && !data.manufacture?.count_validate_from_customer" icon='fas fa-circle' class='text-cyan-500 animate-pulse baseline text-xxs' fixed-width aria-hidden='true' />
                        </dd>
                        <dd v-else class="flex items-start gap-x-2">
                            -
                        </dd>
                    </div>
                    
                    <!-- Jumlah Validasi Customer -->
                    <div v-if="data.product?.type === 'custom'" class="border-l-4 border-gray-400 pl-2.5 flex justify-between gap-x-4 py-3">
                        <dt class="text-gray-500">
                            Jml. validasi dari customer
                        </dt>
                        <dd v-if="data.manufacture?.count_validate_from_customer !== null" class="flex items-center gap-x-2">
                            {{ data.manufacture?.count_validate_from_customer ?? 0 }} pcs
                            <FontAwesomeIcon v-tooltip="'Jumlah pcs yang dimasukkan ke estimasi biaya'" v-if="data.manufacture?.count_validate_from_customer" icon='fas fa-circle' class='text-cyan-500 animate-pulse baseline text-xxs' fixed-width aria-hidden='true' />
                        </dd>
                        <dd v-else class="flex items-start gap-x-2">
                            -
                        </dd>
                    </div>
                </div>

                <!-- Total estimasi biaya -->
                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">
                        Total estimasi biaya
                    </dt>
                    <dd class="flex items-start gap-x-1">
                        <div class="font-medium">{{ useFormatCurrency(data.total_amount) || '-' }}</div>
                    </dd>
                </div>

            </dl>
        </div>
        
        <Fieldset v-if="data.child_order" legend="Produk Tutup" class="h-fit sm:col-span-2 w-full">
            <template #legend>
                <div class="flex items-center gap-x-2 font-bold">
                    <Icon :data="data.child_order.type_icon" />
                    Produk Tutup <span class="font-normal">(tambahan)</span>
                </div>
            </template>

            <dl class="-my-3 divide-y divide-gray-100 px-2 py-2 text-sm/6">

                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Order</dt>
                    <dd class="flex font-medium text-right w-fit">
                        <Link :href="(generateLinkOrder(data.child_order?.slug) as string)" class="primaryLink">
                            <!-- <Icon :data="data.child_order?.type_icon" /> -->
                            {{ data.child_order?.reference || '-' }}
                        </Link>
                    </dd>
                </div>
                
                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Nama produk</dt>
                    <dd class="text-right gap-x-2 font-medium">
                        <div v-if="generateLinkProduct(data.child_order?.product_slug)">
                            <Link :href="(generateLinkProduct(data.child_order?.product_slug) as string)" class="secondaryLink">
                                <Icon :data="data.child_order?.type_icon" />
                                {{ data.child_order?.product?.name || '-' }}
                            </Link>
                            <div class="text-gray-400 font-normal italic">
                                {{ data.child_order?.product?.code ? `(${data.child_order?.product?.code})` : '' }}
                            </div>
                        </div>
                        
                        <span v-else-if="data.child_order?.product_slug" class="">
                            <Icon :data="data.child_order?.type_icon" />
                            {{ data.child_order?.product?.name || '-' }}
                            <div class="text-gray-400 font-normal italic">
                                {{ data.child_order?.product?.code ? `(${data.child_order?.product?.code})` : '' }}
                            </div>
                        </span>

                        <span v-else class="text-gray-400 italic">
                            <Icon :data="data.child_order?.type_icon" />
                            Produk tidak ditemukan
                            <FontAwesomeIcon v-tooltip="'Produk mungkin telah dihapus dari sistem'" icon='fal fa-info-circle' class='text-gray-400' fixed-width aria-hidden='true' />
                        </span>
                    </dd>
                </div>

                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Harga</dt>
                    <dd class="flex font-medium ">
                        {{ useFormatCurrency(data.child_order?.product?.price) || '-' }}<span class="inline font-normal">/pcs</span>
                    </dd>
                </div>

                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Jumlah kuantitas</dt>
                    <dd class="flex items-start gap-x-2 font-medium">
                        {{ data.child_order?.quantity || 0 }} pcs
                    </dd>
                </div>

                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Estimasi biaya Tutup</dt>
                    <dd class="flex font-medium ">
                        {{ useFormatCurrency(data.child_order?.product?.price * data.child_order.quantity) || '-' }}
                    </dd>
                </div>
            </dl>
        </Fieldset>
    </div>
</template>